/**
 * Coming-soon email capture.
 *
 * Posts straight to Klaviyo's client-side subscription endpoint using the store's
 * public API key (company_id), which is safe to expose. Klaviyo answers 202 with an
 * empty body on success.
 *
 * On success the field and the button are both hidden and a status line takes the
 * button's place, styled identically. It is a <p role="status"> rather than a
 * relabelled button because once the sign-up has happened there is no action left
 * to take, and a disabled button announcing itself as a button is misleading.
 *
 * Copy below the form is only ever shown for errors.
 *
 * Every [data-signup] group on the page is wired independently, so the centre form
 * and the desktop corner form work the same way without sharing state.
 */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

class JaComingSoon extends HTMLElement {
  connectedCallback() {
    this.companyId = this.dataset.companyId;
    this.listId = this.dataset.listId;

    for (const group of this.querySelectorAll('[data-signup]')) {
      const form = group.querySelector('form');
      form?.addEventListener('submit', (event) => this.#submit(event, group));

      // Clear a stale error as soon as they start correcting it.
      group.querySelector('input[type="email"]')?.addEventListener('input', () => {
        const error = group.querySelector('[data-error]');
        if (error) error.hidden = true;
      });
    }
  }

  async #submit(event, group) {
    event.preventDefault();

    const input = group.querySelector('input[type="email"]');
    const button = group.querySelector('[type="submit"]');
    const error = group.querySelector('[data-error]');

    const email = input.value.trim();

    if (!EMAIL.test(email)) {
      this.#fail(error, this.dataset.errorInvalid);
      input.focus();
      return;
    }

    if (!this.companyId || !this.listId) {
      this.#fail(error, this.dataset.errorConfig);
      return;
    }

    button.disabled = true;
    if (error) error.hidden = true;

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.companyId)}`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json', revision: '2024-10-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                profile: { data: { type: 'profile', attributes: { email } } },
              },
              relationships: { list: { data: { type: 'list', id: this.listId } } },
            },
          }),
        }
      );

      if (!response.ok) throw new Error(`Klaviyo responded ${response.status}`);
      this.#succeed(group);
    } catch {
      button.disabled = false;
      this.#fail(error, this.dataset.errorGeneric);
    }
  }

  #succeed(group) {
    const form = group.querySelector('form');
    const done = group.querySelector('[data-done]');

    if (done) {
      form.hidden = true;
      done.hidden = false;
      done.focus?.();
    } else {
      // Corner sign-up: no status line of its own, so the whole form steps aside.
      form.hidden = true;
      const thanks = group.querySelector('[data-thanks]');
      if (thanks) {
        thanks.hidden = false;
        thanks.focus?.();
      }
    }
  }

  #fail(error, message) {
    if (!error || !message) return;
    error.textContent = message;
    error.hidden = false;
  }
}

if (!customElements.get('ja-coming-soon')) {
  customElements.define('ja-coming-soon', JaComingSoon);
}
